import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Package, CheckCircle2, RefreshCw } from 'lucide-react';
import { DynamicFieldForm } from '@/components/DynamicFieldRenderer';

export default function StandaloneCreateDialog({
  open,
  onOpenChange,
  form,
  onNameChange,
  onProductTypeChange,
  onPriorityChange,
  onDeliveryDateChange,
  onSpecChange,
  productConfigs,
  fields,
  creating,
  onCreate,
  onPlyChange,
  catalogItems,
  onLoadCatalog,
  onCatalogSelect,
}) {
  const [showCatalogPicker, setShowCatalogPicker] = useState(false);

  const handleToggleCatalogPicker = () => {
    const willShow = !showCatalogPicker;
    setShowCatalogPicker(willShow);
    if (willShow) onLoadCatalog();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Plus className="h-5 w-5 text-teal-600" />
            <span>New Standalone Job Card</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">
                Job Card Name <span className="text-red-400">*</span>
              </Label>
              <Input
                placeholder='e.g., "Burger Box 6-inch Sample" or "Stock Replenishment — Sharma Labels"'
                value={form.name}
                onChange={e => onNameChange(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">
                Product Type <span className="text-red-400">*</span>
              </Label>
              <Select
                value={form.product_type_code}
                onValueChange={onProductTypeChange}
              >
                <SelectTrigger className="h-9"><SelectValue placeholder="Select product type" /></SelectTrigger>
                <SelectContent>
                  {productConfigs.map(pc => (
                    <SelectItem key={pc.code} value={pc.code}>{pc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Priority</Label>
              <Select
                value={form.priority}
                onValueChange={onPriorityChange}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Expected Delivery Date (Optional)</Label>
              <Input
                type="date"
                value={form.expected_delivery_date}
                onChange={e => onDeliveryDateChange(e.target.value)}
                className="h-9"
              />
            </div>
          </div>

          {/* Create from Catalog option */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleCatalogPicker}
              className="text-xs"
            >
              <Package className="h-3.5 w-3.5 mr-1" />
              {showCatalogPicker ? 'Hide Catalog' : 'Create from Catalog'}
            </Button>
            {form.catalog_item_id && (
              <Badge className="text-xs bg-green-50 text-green-700">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Loaded from catalog
              </Badge>
            )}
          </div>

          {showCatalogPicker && (
            <div className="border rounded-lg p-3 bg-slate-50 max-h-48 overflow-y-auto">
              {catalogItems.length === 0 ? (
                <p className="text-xs text-slate-400">No catalog items found</p>
              ) : (
                <div className="space-y-1">
                  {catalogItems.map(ci => (
                    <button
                      key={ci.id}
                      className="w-full text-left px-3 py-2 rounded hover:bg-white text-sm flex items-center justify-between"
                      onClick={() => { onCatalogSelect(ci); setShowCatalogPicker(false); }}
                    >
                      <span className="font-medium text-slate-800">{ci.name}</span>
                      <Badge variant="outline" className="text-[10px]">{ci.product_type}</Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Job Card Fields (appears when product type is selected) */}
          {form.product_type_code && fields.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Job Card Specifications</h4>
              <DynamicFieldForm
                fields={fields}
                values={form.specifications}
                onChange={onSpecChange}
                columns={2}
                onPlyChange={onPlyChange}
                orderType={form.product_type_code}
              />
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={onCreate}
            disabled={creating}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {creating ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            {creating ? 'Creating...' : 'Create Job Card'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
